import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ExternalLink, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface CalendarTabProps {
  company: Company | null;
}

const CalendarTab = ({ company }: CalendarTabProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t("dashboard.calendar.title", "Shared Calendar")}
          </CardTitle>
          <CardDescription>
            {t("dashboard.calendar.subtitle", "View your team's remote work periods in a unified calendar")}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center py-12">
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
            
            <h3 className="text-lg font-semibold">
              {t("dashboard.calendar.googleIntegration", "Google Calendar Integration")}
            </h3>
            <p className="text-muted-foreground text-sm">
              {t("dashboard.calendar.comingSoon", "This feature will be available soon. It will allow:")}
            </p>
            
            <div className="text-left space-y-2 max-w-sm mx-auto">
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>{t("dashboard.calendar.features.sync", "Sync with Google Calendar")}</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>{t("dashboard.calendar.features.viewApproved", "View approved remote work periods")}</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>{t("dashboard.calendar.features.planResources", "Plan resources by country")}</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>{t("dashboard.calendar.features.manageConflicts", "Manage schedule conflicts")}</span>
              </div>
            </div>

            <div className="pt-4 space-y-2">
              <Button disabled className="w-full">
                <Settings className="h-4 w-4 mr-2" />
                {t("dashboard.calendar.configureGoogle", "Configure Google Calendar")}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t("dashboard.calendar.inDevelopment", "Feature in development")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview of what the calendar will look like */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("dashboard.calendar.preview", "Calendar Preview")}
          </CardTitle>
          <CardDescription>
            {t("dashboard.calendar.previewSubtitle", "Here's what the calendar will look like once integrated")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="grid grid-cols-7 gap-2 mb-4">
              {[
                t("dashboard.calendar.days.mon", "Mon"),
                t("dashboard.calendar.days.tue", "Tue"),
                t("dashboard.calendar.days.wed", "Wed"),
                t("dashboard.calendar.days.thu", "Thu"),
                t("dashboard.calendar.days.fri", "Fri"),
                t("dashboard.calendar.days.sat", "Sat"),
                t("dashboard.calendar.days.sun", "Sun")
              ].map((day) => (
                <div key={day} className="text-center text-sm font-medium p-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 35 }, (_, i) => {
                const day = i + 1;
                const hasRemoteWork = [5, 12, 18, 25].includes(day);
                
                return (
                  <div
                    key={i}
                    className={`aspect-square border rounded p-1 text-xs ${
                      hasRemoteWork 
                        ? 'bg-blue-100 border-blue-300 text-blue-800' 
                        : 'bg-background'
                    }`}
                  >
                    {day <= 31 ? (
                      <div>
                        <div>{day}</div>
                        {hasRemoteWork && (
                          <div className="mt-1 text-[10px] truncate">
                            2 {t("dashboard.calendar.remoteWorkCount", "remote work")}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                  <span>{t("dashboard.calendar.remoteWorkDays", "Days with remote work")}</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" disabled>
                <ExternalLink className="h-3 w-3 mr-1" />
                {t("dashboard.calendar.openInGoogle", "Open in Google Calendar")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarTab;
